import { GuidelinesManager } from '@/components/FormFields/GuidelinesManager.tsx';
import SettingsChipsField from '@/components/settings/SettingsChipsField.tsx';
import { SettingTitle } from '@/components/settings/SettingsTitle.tsx';
import { DashboardContext } from '@/contexts/DashboardContext.tsx';
import { ContentRulesConfig } from '@superfunnel/types';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@superfunnel/ui-core/v2/Alert';
import Button from '@superfunnel/ui-core/v2/Button';
import Label from '@superfunnel/ui-core/v2/Label';
import Switch from '@superfunnel/ui-core/v2/Switch';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import { useContext, useEffect, useState } from 'react';

type LocalContentRules = Omit<ContentRulesConfig, 'contentGuidelines'> & {
  contentGuidelines?: Array<{
    id: string;
    title: string;
    value: string;
  }>;
};

export const ContentRulesSettings = () => {
  const { accountAiSettings, updateAccountAiSettings } =
    useContext(DashboardContext);
  const [edit, setEdit] = useState<boolean>(false);
  const [contentRules, setContentRules] = useState<Partial<LocalContentRules>>({
    allowedKeywords: [],
    forbiddenKeywords: [],
    contentGuidelines: [],
    disclaimerText: '',
    autoReferenceDisclaimers: true,
  });

  // Initialize from context
  useEffect(() => {
    if (accountAiSettings?.contentRules) {
      const serverRules = accountAiSettings.contentRules;
      // Since types are updated, contentGuidelines should already be an array
      setContentRules(serverRules as LocalContentRules);
    }
  }, [accountAiSettings?.contentRules]);

  // Check for changes to show/hide save button
  useEffect(() => {
    if (!accountAiSettings?.contentRules) {
      setEdit(
        (contentRules.allowedKeywords?.length ?? 0) > 0 ||
          (contentRules.forbiddenKeywords?.length ?? 0) > 0 ||
          (contentRules.contentGuidelines?.length ?? 0) > 0 ||
          contentRules.disclaimerText !== '' ||
          contentRules.autoReferenceDisclaimers !== true,
      );
      return;
    }

    const current = accountAiSettings.contentRules;
    setEdit(
      JSON.stringify(contentRules.allowedKeywords?.sort()) !==
        JSON.stringify(current.allowedKeywords?.sort()) ||
        JSON.stringify(contentRules.forbiddenKeywords?.sort()) !==
          JSON.stringify(current.forbiddenKeywords?.sort()) ||
        JSON.stringify(contentRules.contentGuidelines) !==
          JSON.stringify(current.contentGuidelines) ||
        contentRules.disclaimerText !== current.disclaimerText ||
        contentRules.autoReferenceDisclaimers !==
          current.autoReferenceDisclaimers,
    );
  }, [contentRules, accountAiSettings?.contentRules]);

  const onSave = () => {
    setEdit(false);
    // Cast to ContentRulesConfig since types are now aligned
    updateAccountAiSettings({
      contentRules: contentRules as ContentRulesConfig,
    });
  };

  const onDiscard = () => {
    setEdit(false);
    if (accountAiSettings?.contentRules) {
      setContentRules(accountAiSettings.contentRules as LocalContentRules);
    } else {
      setContentRules({
        allowedKeywords: [],
        forbiddenKeywords: [],
        contentGuidelines: [],
        disclaimerText: '',
        autoReferenceDisclaimers: true,
      });
    }
  };

  return (
    <div
      className={
        'relative grid h-full w-full max-w-full grid-rows-[1fr_auto] overflow-hidden'
      }
    >
      <div className={'h-full overflow-y-auto'}>
        <div className={'mx-auto flex max-w-161 flex-col gap-5 px-2.5'}>
          <SettingTitle
            title="Content Rules"
            description="Set content guidelines, allowed keywords, and forbidden terms for AI-generated content."
          />

          <div className="flex flex-col gap-5">
            <div className="space-y-2">
              <Label className="text-base-700 text-sm font-medium">
                Content Guidelines
              </Label>
              <p className="mb-2 text-sm text-gray-600">
                Describe general content guidelines, tone, and style
                preferences.
              </p>
              <GuidelinesManager
                guidelines={contentRules.contentGuidelines || []}
                onChange={(guidelines) =>
                  setContentRules({
                    ...contentRules,
                    contentGuidelines: guidelines,
                  })
                }
                titlePlaceholder="Guideline title (e.g., Tone & Voice)"
                valuePlaceholder="Describe the content guideline..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-base-700 text-sm font-medium">
                Allowed Keywords
              </Label>
              <p className="mb-2 text-sm text-gray-600">
                Keywords and terms that should be used in content
              </p>
              <SettingsChipsField
                selected={
                  contentRules.allowedKeywords?.map((keyword) => ({
                    id: keyword,
                    title: keyword,
                  })) || []
                }
                setSelected={(selected) =>
                  setContentRules({
                    ...contentRules,
                    allowedKeywords: selected.map((s) => s.title),
                  })
                }
                options={[]}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-base-700 text-sm font-medium">
                Forbidden Keywords
              </Label>
              <p className="mb-2 text-sm text-gray-600">
                Keywords and terms that should be avoided in content
              </p>
              <SettingsChipsField
                selected={
                  contentRules.forbiddenKeywords?.map((keyword) => ({
                    id: keyword,
                    title: keyword,
                  })) || []
                }
                setSelected={(selected) =>
                  setContentRules({
                    ...contentRules,
                    forbiddenKeywords: selected.map((s) => s.title),
                  })
                }
                options={[]}
              />
            </div>

            <div className="space-y-4 border-t border-gray-200 pt-5">
              <div>
                <Label className="text-base-700 text-sm font-medium">
                  Legal Disclaimers & Compliance
                </Label>
                <p className="mb-2 text-sm text-gray-600">
                  Manage legal disclaimers and automatic compliance referencing
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Disclaimer Text
                </Label>
                <p className="mb-2 text-xs text-gray-500">
                  Add your complete disclaimer text with numbered references (¹,
                  ², ³, etc.)
                </p>
                <TextArea
                  value={contentRules.disclaimerText || ''}
                  onChange={(e) =>
                    setContentRules({
                      ...contentRules,
                      disclaimerText: e.target.value,
                    })
                  }
                  placeholder="® Company Name 2025. All rights reserved...&#10;&#10;¹ First disclaimer section...&#10;&#10;² Second disclaimer section...&#10;&#10;³ Third disclaimer section..."
                  rows={8}
                  className="w-full font-mono text-xs"
                />
              </div>

              <div className="flex items-center space-x-3">
                <Switch
                  checked={contentRules.autoReferenceDisclaimers || false}
                  onCheckedChange={(checked) =>
                    setContentRules({
                      ...contentRules,
                      autoReferenceDisclaimers: checked,
                    })
                  }
                />
                <div>
                  <Label className="text-sm font-medium text-gray-700">
                    Auto-reference disclaimers
                  </Label>
                  <p className="text-xs text-gray-500">
                    Automatically add numbered references (¹, ², ³) to
                    disclaimer sections
                  </p>
                </div>
              </div>

              <Alert variant="info">
                <AlertTitle>How it works:</AlertTitle>
                <AlertDescription>
                  <ul className="space-y-1 text-sm">
                    <li>
                      • Add your complete disclaimer text with numbered sections
                    </li>
                    <li>
                      • AI will automatically add appropriate superscript
                      numbers (¹, ², ³) when needed
                    </li>
                    <li>
                      • Full disclaimer text will be appended to generated
                      content when required
                    </li>
                  </ul>
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </div>
      </div>
      {edit && (
        <div
          className={
            'mx-auto flex w-full max-w-161 justify-end gap-3 bg-white px-2.5 pt-2'
          }
        >
          <Button variant={'outline'} onClick={onDiscard}>
            Discard
          </Button>
          <Button onClick={onSave}>Save</Button>
        </div>
      )}
    </div>
  );
};
